import React, { FC } from 'react';
import { ScrollView, Text, View } from 'react-native';
import i18n from 'i18n-js';
import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { ANNOTATION_TYPE_LABELS } from '@v2/components/imageHandling/components/DrawingCanvas/constants';
import { Annotation, AnnotationType, TextAnnotation } from '@v2/components/imageHandling/components/DrawingCanvas/types';

interface AnnotationListProps extends WithColor {
  annotations: Annotation[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  widthPx: number;
}

const AnnotationList: FC<AnnotationListProps> = ({ C, annotations, selectedId, onSelect, widthPx }) => {
  return (
    <View
      testID={TEST_IDS.drawingCanvas.annotationList}
      style={{
        flex: 1,
        width: widthPx,
        backgroundColor: C('Background/Light/Panel [web]'),
        borderLeftWidth: 1,
        borderLeftColor: C('Content/Light/Tertiary'),
      }}
    >
      <View
        style={{
          paddingHorizontal: scale(12),
          paddingVertical: scale(10),
          borderBottomWidth: 1,
          borderBottomColor: C('Content/Light/Tertiary'),
        }}
      >
        <Text style={[font['Button 2'], { color: C('Label/Light/Primary') }]}>{i18n.t('Annotations')} ({annotations.length})</Text>
      </View>
      <ScrollView style={{ flex: 1 }}>
        {annotations.length === 0 ? (
          <Text style={[font['Caption 1'], { padding: scale(12), color: C('Label/Light/Tertiary') }]}>
            {i18n.t('Draw on the image to create annotations.')}
          </Text>
        ) : null}
        {annotations.map((a, index) => (
          <TouchableElement
            key={a.id}
            testID={TEST_IDS.drawingCanvas.annotationListItem}
            onPress={() => onSelect(selectedId === a.id ? null : a.id)}
          >
            {({ boxStyle }) => (
              <View
                style={[
                  boxStyle,
                  {
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: scale(8),
                    paddingHorizontal: scale(12),
                    paddingVertical: scale(8),
                    backgroundColor: selectedId === a.id ? C('Button/Light/Secondary') : 'transparent',
                    borderBottomWidth: 1,
                    borderBottomColor: C('Content/Light/Tertiary'),
                  },
                ]}
              >
                <View style={{ width: scale(10), height: scale(10), borderRadius: scale(5), backgroundColor: a.style.strokeColor }} />
                <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), flex: 1 }]} numberOfLines={1}>
                  {i18n.t(ANNOTATION_TYPE_LABELS[a.type])}
                  {a.type === AnnotationType.Text ? ` "${(a as TextAnnotation).text}"` : ` #${index + 1}`}
                </Text>
              </View>
            )}
          </TouchableElement>
        ))}
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(AnnotationList);
